import React, { useEffect, useMemo, useState } from "react";
import { FiltersButton, TableHeader } from "@services/ui-components";
import { useTranslation } from "react-i18next";
import { Button, Col, Row, Tab, Tabs } from "react-bootstrap";
import { currentBranchIdVar } from "@me-team/host/main/globalVar/state";
import { useNavigate, useParams } from "react-router-dom";
import { apolloClientMeBooking } from "@me-team/host/src/apollo/apollo-client";
import {
    useGetRestaurantAddRequestsQuery,
    useGetRestaurantDeleteRequestsQuery,
} from "@me-team/host/meBooking/restaurantRequest/graphql/restaurantRequest.hooks";
import { useCloseTooltipsOnModalOpen } from "@me-team/common/src/hooks/useCloseTooltipsOnModalOpen";
import RequestModal from "./RequestModal";
import RequestsTable from "./RequestsTable";
import { TaggedAddItem, TaggedDeleteItem, TaggedRequestItem } from "./types";
import { PER_PAGE } from "./constants";

const RestaurantRequest: React.FC = () => {
    const { t } = useTranslation();
    const [showFilters, setShowFilters] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedVenue, setSelectedVenue] = useState("");
    const [activeTab, setActiveTab] = useState("AllRequests");
    const [addPage, setAddPage] = useState(1);
    const [deletePage, setDeletePage] = useState(1);
    const [selectedRequest, setSelectedRequest] = useState<TaggedRequestItem | null>(null);
    const [showModal, setShowModal] = useState(false);
    const { tooltipResetKey, resetTooltips } = useCloseTooltipsOnModalOpen();

    const toggleFilters = () => setShowFilters(!showFilters);
    const { branchId } = useParams();
    const navigate = useNavigate();
    const currentBranchId = branchId ?? currentBranchIdVar();

    useEffect(() => {
        if (!branchId) {
            navigate(`/me-booking/restaurant-request/${currentBranchId}`);
        }
    }, [branchId, currentBranchId, navigate]);

    const {
        data: addData,
        loading: addLoading,
        refetch: refetchAdd,
    } = useGetRestaurantAddRequestsQuery({
        variables: { search: searchTerm || undefined, page: addPage, perPage: PER_PAGE },
        client: apolloClientMeBooking,
    });

    const {
        data: deleteData,
        loading: deleteLoading,
        refetch: refetchDelete,
    } = useGetRestaurantDeleteRequestsQuery({
        variables: { search: searchTerm || undefined, page: deletePage, perPage: PER_PAGE },
        client: apolloClientMeBooking,
    });

    const addItems: TaggedAddItem[] = (addData?.restaurantAddRequests?.requests ?? []).map((r) => ({
        ...r,
        requestType: "add" as const,
    }));
    const deleteItems: TaggedDeleteItem[] = (deleteData?.restaurantDeleteRequests?.requests ?? []).map((r) => ({
        ...r,
        requestType: "delete" as const,
    }));

    const venueOptions = useMemo(() => {
        const source =
            activeTab === "AddRequests"
                ? addItems
                : activeTab === "DeleteRequests"
                  ? deleteItems
                  : [...addItems, ...deleteItems];
        return [...new Set(source.map((i) => i.venueName).filter(Boolean))];
    }, [activeTab, addItems, deleteItems]);

    useEffect(() => {
        setSelectedVenue("");
        setAddPage(1);
        setDeletePage(1);
    }, [activeTab]);

    const filteredAddItems = selectedVenue ? addItems.filter((i) => i.venueName === selectedVenue) : addItems;
    const filteredDeleteItems = selectedVenue ? deleteItems.filter((i) => i.venueName === selectedVenue) : deleteItems;

    const addTotal = selectedVenue ? filteredAddItems.length : (addData?.restaurantAddRequests?.totalCount ?? 0);
    const deleteTotal = selectedVenue
        ? filteredDeleteItems.length
        : (deleteData?.restaurantDeleteRequests?.totalCount ?? 0);
    const allTotal = addTotal + deleteTotal;

    const filter = searchTerm || selectedVenue ? "active" : "";

    const handleShowModal = (item: TaggedRequestItem) => {
        resetTooltips();
        setSelectedRequest(item);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        resetTooltips();
        setShowModal(false);
        setSelectedRequest(null);
    };

    const handleRefetch = async () => {
        await refetchAdd();
        await refetchDelete();
    };

    const handleApplyFilter = () => {
        setAddPage(1);
        setDeletePage(1);
    };

    const handleClearFilter = () => {
        setSearchTerm("");
        setSelectedVenue("");
        setAddPage(1);
        setDeletePage(1);
    };

    const tableProps = { onRowClick: handleShowModal, tooltipResetKey };

    return (
        <>
            <TableHeader
                title={t("Restaurant Request")}
                paths={[
                    { path: "", label: "ME-Booking" },
                    { path: "", label: t("Restaurant Request") },
                ]}
                buttons={
                    <Row className="gx-3 justify-content-end mt-3 mt-lg-0">
                        <Col xs={5} md={6} lg={5} xxl={4}>
                            <FiltersButton
                                filter={filter}
                                showFilters={showFilters}
                                onClick={toggleFilters}
                                classesText="d-none d-sm-inline-block"
                            />
                        </Col>
                    </Row>
                }
                showFilters={showFilters}
                colSize={7}
                btnColSize={5}
            >
                <Row className="gx-4 pb-6">
                    <Col lg={8} xl={9}>
                        <Row className="gx-3">
                            <Col md={6}>
                                <div className="position-relative mb-3 mb-lg-0">
                                    <input
                                        type="text"
                                        className="form-control rounded-1"
                                        placeholder={t("Search")}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        value={searchTerm}
                                    />
                                    <i className="bi bi-search position-absolute top-50 end-0 translate-middle-y text-dark me-3"></i>
                                </div>
                            </Col>
                            <Col md={6} className="mb-3 mb-lg-0">
                                <select
                                    className="form-select rounded-1"
                                    value={selectedVenue}
                                    onChange={(e) => setSelectedVenue(e.target.value)}
                                >
                                    <option value="">{t("All venues")}</option>
                                    {venueOptions.map((name) => (
                                        <option key={name} value={name}>
                                            {name}
                                        </option>
                                    ))}
                                </select>
                            </Col>
                        </Row>
                    </Col>
                    <Col lg={4} xl={3}>
                        <Row className="gx-3 h-100">
                            <Col xs={6}>
                                <Button
                                    variant="outline-dark"
                                    className="border-grey w-100 h-100 fw-normal rounded-1"
                                    onClick={handleClearFilter}
                                >
                                    {t("clear")}
                                </Button>
                            </Col>
                            <Col xs={6}>
                                <Button
                                    variant="primary"
                                    className="w-100 h-100 rounded-1 fw-normal px-2"
                                    onClick={handleApplyFilter}
                                >
                                    {t("apply")}
                                </Button>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </TableHeader>

            <Tabs
                defaultActiveKey="AllRequests"
                id="restaurant-request-tabs"
                className="mb-4"
                justify
                onSelect={(k) => setActiveTab(k || "AllRequests")}
            >
                <Tab eventKey="AllRequests" title={`${t("All Requests")} (${allTotal})`}>
                    <RequestsTable
                        items={[...filteredAddItems, ...filteredDeleteItems]}
                        loading={addLoading || deleteLoading}
                        totalCount={allTotal}
                        currentPage={addPage}
                        pageCount={addData?.restaurantAddRequests?.pageCount ?? "0"}
                        onPageChange={setAddPage}
                        {...tableProps}
                    />
                </Tab>

                <Tab eventKey="AddRequests" title={`${t("Add venue")} (${addTotal})`}>
                    <RequestsTable
                        items={filteredAddItems}
                        loading={addLoading}
                        totalCount={addTotal}
                        currentPage={addPage}
                        pageCount={addData?.restaurantAddRequests?.pageCount ?? "0"}
                        onPageChange={setAddPage}
                        {...tableProps}
                    />
                </Tab>

                <Tab eventKey="DeleteRequests" title={`${t("Delete venue")} (${deleteTotal})`}>
                    <RequestsTable
                        items={filteredDeleteItems}
                        loading={deleteLoading}
                        totalCount={deleteTotal}
                        currentPage={deletePage}
                        pageCount={deleteData?.restaurantDeleteRequests?.pageCount ?? "0"}
                        onPageChange={setDeletePage}
                        {...tableProps}
                    />
                </Tab>
            </Tabs>

            <RequestModal
                show={showModal}
                handleClose={handleCloseModal}
                request={selectedRequest}
                refetch={handleRefetch}
            />
        </>
    );
};

export default RestaurantRequest;
